/** @file moveFocus: focuses the next or previous control of the panel, found by data-field after a re-render. */

const TABBABLE = 'input, select, textarea, button, [tabindex]';
const RETRIES = 6;

/** Enabled, tabbable, and not folded away inside a closed `<details>` (its `<summary>` stays reachable). */
function reachable(el: HTMLElement): boolean {
  if ((el as HTMLInputElement).disabled || el.getAttribute('tabindex') === '-1') return false;
  const folded = el.closest('details:not([open])');
  return folded === null || el.closest('summary') !== null;
}

/** Looks the target up again by its stable `data-field` id (the element may have been re-created), else uses the element. */
function resolve(id: string | null, el: HTMLElement): HTMLElement | null {
  if (id === null) return el.isConnected ? el : null;
  return [...document.querySelectorAll<HTMLElement>('[data-field]')].find((f) => f.getAttribute('data-field') === id) ?? null;
}

/** Focuses `target` once a re-render has put it in the page; retries a few times for a late render. */
function focusSoon(id: string | null, el: HTMLElement, tries: number): void {
  setTimeout(() => {
    const target = resolve(id, el);
    if (target !== null) { target.focus(); return; }
    if (tries > 0) focusSoon(id, el, tries - 1);
  }, 0);
}

/**
 * Moves focus from `from` to the next (`dir` 1) or previous (-1) tabbable control inside its panel and returns true
 * (the caller then cancels the native Tab). False when there is none, so the native Tab proceeds. The target is found
 * by its `data-field` id after the commit's re-render, never by the element reference.
 */
export function moveFocus(from: HTMLElement, dir: 1 | -1): boolean {
  const root: ParentNode = from.closest('[data-panel]') ?? document;
  const list = [...root.querySelectorAll<HTMLElement>(TABBABLE)].filter(reachable);
  const at = list.indexOf(from);
  const target = at < 0 ? undefined : list[at + dir];
  if (target === undefined) return false;
  focusSoon(target.getAttribute('data-field'), target, RETRIES);
  return true;
}
